import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { map } from 'rxjs';
import { IAddress } from '@app/api/interfaces/address.interface';
import { ILocationForm } from '@app/main/main-content/location/interfaces/location-form.interface';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { MapWrapperComponent } from '@app/main/main-content/map/components/map-wrapper/map-wrapper.component';
import { MapDataAdapter } from '@app/main/main-content/map/services/map-data.adapter';
import { AddressSearchComponent } from '@app/main/main-content/location/components/address-search/address-search.component';
import { DialogEventLocationMapService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-location-map.service';
import { DialogEventState } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-state.service';
import { DialogEventTabsService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-tabs.service';
import { LocationPinEditService } from '@app/main/main-content/location/services/location-pin-edit.service';
import { MapEditToolbarComponent } from '@app/main/main-content/map/components/map-edit-toolbar/map-edit-toolbar.component';
import { isSamePosition } from '@app/main/main-content/map/utils/geometry';

@Component({
  selector: 'gc-dialog-event-location-edit',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MapWrapperComponent,
    TranslateDirective,
    TranslatePipe,
    Button,
    SvgIconComponent,
    AddressSearchComponent,
    MapEditToolbarComponent,
  ],
  providers: [
    DialogEventLocationMapService,
    { provide: MapDataAdapter, useExisting: DialogEventLocationMapService },
    LocationPinEditService,
  ],
  templateUrl: './dialog-event-location-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full min-h-0' },
})
export class DialogEventLocationEditComponent {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tabs = inject(DialogEventTabsService);
  private readonly formatting = inject(FormattingDataService);
  private readonly locationMap = inject(DialogEventLocationMapService);

  protected readonly dialogState = inject(DialogEventState);
  protected readonly pinEdit = inject(LocationPinEditService);

  // no address is fine — `GET /addresses` may find none for the pin, and the event then saves `location: null`
  protected readonly form = this.fb.group<ILocationForm>({
    location: this.fb.nonNullable.control(this.dialogState.draft().location ?? ''),
    coordinates: this.fb.nonNullable.control(this.dialogState.draft().coordinates),
  });

  private readonly formValue = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue())), {
    initialValue: this.form.getRawValue(),
  });

  protected readonly location = computed(() => this.formValue().location);

  // a pin moved and brought back with `Undo` or `X` is the saved location again — nothing to save
  private readonly hasLocationChanged = computed(() => {
    const { location, coordinates } = this.formValue();
    const saved = this.dialogState.draft();

    return (location.trim() || null) !== saved.location || !isSamePosition(coordinates, saved.coordinates);
  });

  /** a large move has no address until the map's checkmark looks it up */
  protected readonly canSave = computed(() => this.hasLocationChanged() && !this.pinEdit.isLargeMove());

  protected readonly latitude = computed(() => this.formatCoordinate(this.pinEdit.position()?.lat));
  protected readonly longitude = computed(() => this.formatCoordinate(this.pinEdit.position()?.lon));

  constructor() {
    this.pinEdit.init(this.form);

    this.locationMap.mapClick$.pipe(takeUntilDestroyed()).subscribe(({ position }) => this.pinEdit.move(position));
    this.pinEdit.relocated$.pipe(takeUntilDestroyed()).subscribe(position => this.locationMap.pinTrigger(position));

    effect(() => this.locationMap.pinPosition.set(this.pinEdit.position()));
  }

  protected onAddressSelected(address: IAddress): void {
    this.pinEdit.relocate(address);
  }

  protected save(): void {
    if (!this.canSave()) return;

    const { location, coordinates } = this.form.getRawValue();
    const value = { location: location.trim() || null, coordinates };

    // nothing to save it to yet: the location goes out with `Create event`
    if (this.dialogState.isCreating()) {
      this.dialogState.form.patchValue(value);
      this.tabs.closeEditor();

      return;
    }

    this.dialogState
      .saveDraft({ ...this.dialogState.draft(), ...value })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(saved => {
        if (saved) this.tabs.closeEditor();
      });
  }

  protected cancel(): void {
    this.tabs.closeEditor();
  }

  private formatCoordinate(value: number | undefined): string {
    return this.formatting.formatDecimalDegrees(value);
  }
}
